/*      Dara - Organisation
 * All axios endpoints for organisations. They will be prefixed with /api/organisations/
 */
import axios from '@/plugins/axios'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import type {
  IGetLicences,
  IGetSafWizardDetails,
  ISaveSafScores,
  ISetDefaultPassword,
  IGetOrganisationEdit,
} from '@/interfaces/api/Dara/Organisation'
import type IMy1CStudentProgrammeOverview from '@/interfaces/api-resources/Dara/My1CStudentProgrammeOverview'
import type { IMyAStudentProgrammeOverview } from '@/interfaces/api-resources/Dara/MyAStudentProgrammeOverview'
import type { IStudentFilter } from '@/interfaces/api/Dara/StudentFilter'
import type ICentigradeOverview from '@/interfaces/api-resources/MyUC/CentigradeOverview'
import type IMyCCStudentProgrammeOverview from '@/interfaces/api-resources/Dara/MyCCStudentProgrammeOverview'
import type OrganisationProgramme from '@/interfaces/api-resources/Dara/OrganisationProgramme'
import type IDataResponse from '@/interfaces/api-resources/DataResponse'
import type IDataAdditionalResponse from '@/interfaces/api-resources/DataAdditionalResponse'
import type IOrganisationEnableLicencedProgrammes from '@/interfaces/api-resources/Dara/OrganisationEnableLicencedProgrammes'
import { serialize } from '@/composable/useDataConverter'
import type IDropdownOption from '@/interfaces/internal-resources/DropdownOption'
import type { IStudentSafSaveData } from '@/interfaces/api-resources/Dara/Student'

interface OrganisationRequiredSettingsResponse {
  is_year_selection_required: boolean;
  is_subgroup_selection_required: boolean;
}

interface OrganisationRemovePasswordResponse {
  first_name: string;
  last_name: string;
}

interface OrganisationAssignLicencesResponse {
  queued: boolean;
  success: boolean;
}

interface OrganisationSummaryResultsExportRequest {
  organisation_academic_year_id: number | 'all';
  programme_id: number;
  student_ids?: number[];
  year_group_id?: number
  sub_group_id?: number
}

interface OrganisationStudentProgrammeMy1COverviewRequest {
  student_programme_ids: number[];
}

interface OrganisationStudentProgrammeMyAOverviewRequest {
  student_programme_ids: number[];
}

interface OrganisationStudentProgrammeMyUCOverviewRequest {
  student_programme_ids: number[];
}

interface OrganisationStudentProgrammeMyCCOverviewRequest {
  student_programme_ids: number[];
}

interface RegenerateReportsDryRunResponse {
  completed_student_programmes: number
}

export default {
  baseUrl() {
    const appStore = useAppStore()

    return `${appStore.daraApiBaseUrl}/organisations/`
  },

  addDefaultPasswordToExistingStudents(id: number) {
    return axios.post(this.baseUrl() + id + '/default-password-existing-students')
  },

  setDefaultPasswordToSelectedStudents(id: number, data: object) {
    return axios.post(this.baseUrl() + id + '/students-default-password', data)
  },

  setCustomPasswordToSelectedStudents(id: number, data: object) {
    return axios.post(this.baseUrl() + id + '/students-custom-password', data)
  },

  assignLicences(id: number, data: object) {
    return axios.post<OrganisationAssignLicencesResponse, OrganisationAssignLicencesResponse>(this.baseUrl() + id + '/students/licences', data)
  },

  assignLicencesCreate(id: number, data: object) {
    return axios.post<
      IDataResponse<Domain.Students.Resources.ApiV3.StudentCompatibleLicenceTypeResource[]>,
      IDataResponse<Domain.Students.Resources.ApiV3.StudentCompatibleLicenceTypeResource[]>
    >(this.baseUrl() + id + '/students/licences/create', data)
  },

  enableLicencedProgrammes(id: number, data: object) {
    return axios.post<IOrganisationEnableLicencedProgrammes, IOrganisationEnableLicencedProgrammes>(this.baseUrl() + id + '/students/programmes', data)
  },

  enableLicencedProgrammesDryRun(id: number, data: object) {
    return axios.post<IOrganisationEnableLicencedProgrammes, IOrganisationEnableLicencedProgrammes>(this.baseUrl() + id + '/students/programmes-dry-run', data)
  },

  deleteLogo(id: number) {
    return axios.delete(this.baseUrl() + id + '/logo')
  },

  deleteBackground(id: number) {
    return axios.delete(this.baseUrl() + id + '/background')
  },

  getDefaultProgrammeSettings(id: number) {
    return axios.get<OrganisationProgramme[]>(this.baseUrl() + id + '/default-programmes-settings')
      .then(({ data }) => data)
  },

  getOrganisationEdit(id: number) {
    return axios.get<IGetOrganisationEdit, IGetOrganisationEdit>(this.baseUrl() + id + '/settings/edit')
  },

  getOrganisationSettings(organisationId: number) {
    return axios.get<
      IDataResponse<App.Http.Resources.ApiV3.OrganisationSettingResource>,
      IDataResponse<App.Http.Resources.ApiV3.OrganisationSettingResource>
    >(this.baseUrl() + `${organisationId}/organisation-settings`)
      .then(({ data }) => data)
  },

  getLicences(id: number, searchParams: string) {
    return axios.get<IGetLicences, IGetLicences>(this.baseUrl() + id + '/licences' + '?' + searchParams)
  },

  getLicencesExport(id: number, searchParams: string)  {
    return axios.get<Blob, Blob>(this.baseUrl() + id + '/licences' + '?' + searchParams, { responseType: 'blob' })
  },

  getLicenceTypes(id: number) {
    interface IGetLicenceTypesAdditionalResponse {
      open_licences_purchased_count: number
      open_licences_unused_count: number
      open_licence_adjustments_count: number
      has_unlimited_my1c_trials: boolean
    }

    return axios.get<IDataAdditionalResponse<Domain.Licences.Resources.ApiV3.LicenceTypeCountExtraResource[], IGetLicenceTypesAdditionalResponse>, IDataAdditionalResponse<Domain.Licences.Resources.ApiV3.LicenceTypeCountExtraResource[], IGetLicenceTypesAdditionalResponse>>(
      `${this.baseUrl()}${id}/licence-types`,
    )
  },

  getProgrammes(id: number, is_interviews_handled?: boolean) {
    const searchParams = new URLSearchParams()

    if (is_interviews_handled) {
      searchParams.append('is_interviews_handled', is_interviews_handled.toString())
    }

    return axios.get<
      IDataResponse<App.Http.Resources.ApiV3.ProgrammeV3Resource[]>,
      IDataResponse<App.Http.Resources.ApiV3.ProgrammeV3Resource[]>
    >(this.baseUrl() + id + '/programmes' + '?' + searchParams.toString())
      .then(data => data.data)
  },

  getAllStudentIds(id: number, filters: IStudentFilter) {
    // filter out empty strings and optional booleans
    const cleanedFilters = Object.fromEntries(Object.entries(filters)
      .filter(([key, value]) => key !== 'only_has_left' || value === true)
      .filter(([key]) => key !== 'include_trial')
      .filter(([, value]) => value !== ''),
    )

    return axios.get<IDataResponse<number[]>, IDataResponse<number[]>>(this.baseUrl() + id + '/students/ids', { params: cleanedFilters } )
      .then(data => data.data ?? [])
  },

  getStudentProgrammeMy1COverview(id: number, requestData: OrganisationStudentProgrammeMy1COverviewRequest) {
    const query = serialize(requestData).toString()

    return axios.get<IDataResponse<IMy1CStudentProgrammeOverview[]>, IDataResponse<IMy1CStudentProgrammeOverview[]>>(this.baseUrl() + id + '/student-programmes-my1c?' + query)
      .then(data => data.data ?? [])
  },

  getStudentProgrammeMyAOverview(id: number, programmeId: number, requestData: OrganisationStudentProgrammeMyAOverviewRequest) {
    const query = serialize(requestData).toString()

    return axios.get<IMyAStudentProgrammeOverview, IMyAStudentProgrammeOverview>(this.baseUrl() + id + '/mya-programmes/' + programmeId + '/student-programmes?' + query)
      .then(data => data ?? [])
  },

  getStudentProgrammeMyUCOverview(id: number, requestData: OrganisationStudentProgrammeMyUCOverviewRequest) {
    const query = serialize(requestData).toString()

    return axios.get<IDataResponse<ICentigradeOverview[]>, IDataResponse<ICentigradeOverview[]>>(this.baseUrl() + id + '/student-programmes-myuc?' + query)
      .then(data => data.data ?? [])
  },

  getStudentProgrammeMyCCOverview(id: number, programmeId: number, requestData: OrganisationStudentProgrammeMyCCOverviewRequest) {
    const query = serialize(requestData).toString()

    return axios.get<IDataResponse<IMyCCStudentProgrammeOverview[]>, IDataResponse<IMyCCStudentProgrammeOverview[]>>(this.baseUrl() + id + '/mycc-programmes/' + programmeId + '/student-programmes?' + query)
      .then(data => data.data ?? [])
  },

  getSafData(
    id: number,
    data: { academic_year_id: number | null, group_id: string | number | null, type: string },
  ) {
    const queryString = `?type=${data.type}`

    const searchParams = new URLSearchParams(queryString)

    if (data.academic_year_id) {
      searchParams.append('academic_year_id', data.academic_year_id.toString())
    }

    if (data.group_id) {
      searchParams.append('group_id', data.group_id.toString())
    }

    return axios.get<
      IDataAdditionalResponse<Domain.Students.Resources.ApiV3.StudentSafResource[],{ saf_codes: IDropdownOption[] }>,
      IDataAdditionalResponse<Domain.Students.Resources.ApiV3.StudentSafResource[],{ saf_codes: IDropdownOption[] }>
    >(this.baseUrl() + `${id}/students/saf/data?${searchParams.toString()}`)
  },

  getSafSpreadsheet(
    id: number,
    data: { academic_year_id: number | null, group_id: string | number | null, type: string },
  ) {
    const queryString = `?type=${data.type}`

    const searchParams = new URLSearchParams(queryString)

    if (data.academic_year_id) {
      searchParams.append('academic_year_id', data.academic_year_id.toString())
    }

    if (data.group_id) {
      searchParams.append('group_id', data.group_id.toString())
    }

    return axios.get<Blob, Blob>(this.baseUrl() + `${id}/students/saf/data?${searchParams.toString()}`, { responseType: 'blob' })
  },

  getSafWizardDetails(id: number) {
    return axios.get<IGetSafWizardDetails, IGetSafWizardDetails>(this.baseUrl() + `${id}/saf-wizard-details`)
  },

  getSubjectSelectionNotes() {
    const authStore = useAuthStore()

    return axios.get<string, string>(this.baseUrl() + authStore.selected_organisation_id + '/subject-selection-notes')
  },

  getStudentInterestsAreaExcel(id: number, data: object) {
    const query = serialize(data).toString()

    return axios.get<Blob, Blob>(this.baseUrl() + `${id}/export-subject-interests?${query}`, { responseType: 'blob' })
  },

  getStudentInterestsAreaExcelForYearGroups(id: number, data: object) {
    const query = serialize(data).toString()

    return axios.get<Blob, Blob>(this.baseUrl() + `${id}/export-subject-interests-year-groups?${query}`, { responseType: 'blob' })
  },

  regenerateStudentReports(id: number, data: object) {
    return axios.post(this.baseUrl() + `${id}/regenerated-reports`, data)
  },

  regenerateStudentReportsDryRun(id: number, data: object) {
    return axios.post<RegenerateReportsDryRunResponse, RegenerateReportsDryRunResponse>(this.baseUrl() + `${id}/regenerated-reports-dry-run`, data)
  },

  generateSummaryReport(id: number, data: object) {
    return axios.post(this.baseUrl() + `${id}/create-summary-report-pdf`, data)
  },

  generateSummaryResultsExport(id: number, data: OrganisationSummaryResultsExportRequest) {
    const query = serialize(data).toString()

    return axios.get<Blob, Blob>(this.baseUrl() + `${id}/export-summary-results?${query}`, { responseType: 'blob' })
  },

  importSafSpreadsheet(id: number, data: FormData) {
    return axios.post(this.baseUrl() + `${id}/saf-spreadsheet-data`, data)
  },

  removeDefaultPassword(id: number) {
    return axios.delete<OrganisationRemovePasswordResponse[]>(this.baseUrl() + id + '/default-password')
  },

  saveSettings(id: number, data: object) {
    return axios.patch(this.baseUrl() + id + '/settings', data)
  },

  saveRequiredSettings(id: number, data: object) {
    return axios.put<OrganisationRequiredSettingsResponse, OrganisationRequiredSettingsResponse>(this.baseUrl() + id + '/required-settings', data)
  },

  saveSubjectSelectionNotes(data: object) {
    const authStore = useAuthStore()

    return axios.put(this.baseUrl() + authStore.selected_organisation_id + '/subject-selection-notes', data)
  },

  setDefaultPassword(id: number, data: { default_password: string }) {
    return axios.post<ISetDefaultPassword, ISetDefaultPassword>(this.baseUrl() + id + '/default-password', data)
  },

  setDefaultPasswordToExistingStudents(id: number) {
    return axios.post(this.baseUrl() + id + '/default-password-existing-students')
  },

  setStudentsHasLeft(id: number, data: object) {
    return axios.post(this.baseUrl() + id + '/students-has-left', data)
  },

  setStudentsHasSen(id: number, data: object) {
    return axios.post(this.baseUrl() + id + '/students-has-sen', data)
  },

  restoreStudentsHasLeft(id: number, data: object) {
    return axios.post(this.baseUrl() + id + '/restore-students-has-left', data)
  },

  saveSafScores(id: number, data: { students: IStudentSafSaveData[] }) {
    return axios.post<ISaveSafScores, ISaveSafScores>(this.baseUrl() + `${id}/students/saf`, data)
  },

  uploadLogo(id: number, formData: FormData) {
    return axios.post(this.baseUrl() + id + '/logo', formData)
  },

  uploadBackground(id: number, data: object) {
    return axios.post(this.baseUrl() + id + '/background', data)
  },
}
